import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/store.js';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const setSession = useAuth((s) => s.setSession);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPendingMessage(null);
    setBusy(true);
    try {
      const res = await api.login(email, password);
      setSession(res.user, res.accessToken, res.refreshToken);
    } catch (err) {
      if (err instanceof ApiError && (err.data as { error?: string } | null)?.error === 'pending_approval') {
        setPendingMessage(t('auth.pendingApproval'));
      } else {
        setError(err instanceof ApiError ? `${t('auth.loginFailed')} (${err.status})` : t('auth.networkError'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitRegister(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPendingMessage(null);
    setBusy(true);
    try {
      const locale = i18n.language.startsWith('en') ? 'en' : 'nl';
      const res = await api.register(email, password, displayName, locale);
      if ('pending' in res) {
        setPendingMessage(t('auth.pendingAfterRegister'));
        setMode('login');
      } else {
        setSession(res.user, res.accessToken, res.refreshToken);
      }
    } catch (err) {
      setError(err instanceof ApiError ? `${t('auth.registerFailed')} (${err.status})` : t('auth.networkError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <form onSubmit={mode === 'login' ? submitLogin : submitRegister} style={{ width: 360 }} className="card">
        <h2>{t('app.title')}</h2>
        {pendingMessage && <div className="card" style={{ marginBottom: 12 }}>{pendingMessage}</div>}
        {error && <div className="error-banner">{error}</div>}
        {mode === 'register' && (
          <div style={{ marginBottom: 12 }}>
            <label>{t('auth.displayName')}</label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label>{t('auth.email')}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>{t('auth.password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>
        <button type="submit" disabled={busy} style={{ width: '100%' }}>
          {mode === 'login' ? t('auth.login') : t('auth.register')}
        </button>
        <div className="muted" style={{ marginTop: 16, fontSize: 13, textAlign: 'center' }}>
          {mode === 'login' ? (
            <a href="#" onClick={(e) => { e.preventDefault(); setMode('register'); setError(null); setPendingMessage(null); }}>
              {t('auth.noAccount')}
            </a>
          ) : (
            <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); setError(null); setPendingMessage(null); }}>
              {t('auth.haveAccount')}
            </a>
          )}
        </div>
      </form>
    </div>
  );
}
